import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/uxp-photoshop/uxp-photoshop/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "uxp-manifest-v4"
    }}>{`UXP Manifest v4`}</h1>
    <p>{`The plugin's manifest is where you include metadata about your plugin. Simply put, the manifest is a list of facts about your plugin in the form of a JSON object. No executable code goes into your manifest.`}</p>
    <p>{`The manifest is located in your plugin's root folder and must be named `}<inlineCode parentName="p">{`manifest.json`}</inlineCode>{`.`}</p>
    <p>{`If you are a bit rusty at reading JSON, `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Learn/JavaScript/Objects/JSON"
      }}>{`here's a good reference`}</a>{`.`}</p>
    <p>{`UXP 6.0 has introduced `}<a parentName="p" {...{
        "href": "../manifest-v5/"
      }}>{`manifest v5`}</a>{`. `}</p>
    <h2 {...{
      "id": "example-manifest-v4"
    }}>{`Example manifest (v4)`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "manifestVersion": 4,
    "id": "YOUR_ID_HERE",
    "name": "Name of your plugin",
    "version": "1.0.0",
    "main": "index.html",
    "host": {
        "app": "PS",
        "minVersion": "23.0.0"
    },
    "entrypoints": [
        {
            "type": "command",
            "id": "commandFn",
            "label": {
                "default": "Show A Dialog"
            }
        },
        {
            "type": "panel",
            "id": "panelName",
            "label": {
                "default": "Panel Name"
            },
            "minimumSize": {"width": 230, "height": 200},
            "maximumSize": {"width": 2000, "height": 2000},
            "preferredDockedSize": {"width": 230, "height": 300},
            "preferredFloatingSize": {"width": 230, "height": 300},
            "icons": [
                {"width":23,"height":23,"path":"icons/dark.png","scale":[1,2],"theme":["darkest","dark","medium"]},
                {"width":23,"height":23,"path":"icons/light.png","scale":[1,2],"theme":["lightest","light"]}
            ]
        }
    ],
    "icons": [
        { "width": 23, "height": 23, "path": "icons/icon_D.png", "scale": [ 1, 2 ], "theme": [ "dark", "darkest" ], "species": [ "generic" ] },
        { "width": 23, "height": 23, "path": "icons/icon_N.png", "scale": [ 1, 2 ], "theme": [ "lightest", "light" ], "species": [ "generic" ] }
    ]
}
`}</code></pre>
    <p>{`See the sections below to learn more about each key/value field. All fields are required unless otherwise noted below.`}</p>
    <h2 {...{
      "id": "top-level-metadata"
    }}>{`Top-level metadata`}</h2>
    <p>{`The top level of the manifest JSON object contains high-level information about your plugin.`}</p>
    <p>{`Here are the definitions of the keywords in the "Required" column:`}</p>
    <ul>
      <li parentName="ul">{`Develop - required field for the plugin to be loaded.`}</li>
      <li parentName="ul">{`Publish - required field for plugins to be submitted in the Adobe Developer Console and published in the Plugin Manager.`}</li>
    </ul>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Key path`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Required`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`manifestVersion`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`number`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The version of the manifest. For Photoshop, this should be `}<inlineCode parentName="td">{`4`}</inlineCode>{` or higher.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Develop / Publish`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Unique identifier for your plugin. You can get your unique ID on the `}<a parentName="td" {...{
              "href": "https://console.adobe.io/projects"
            }}>{`Adobe Developer Console`}</a>{`.%7C Develop / Publish`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`name`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The name should be 3 - 45 characters. We recommend your plugin name matches the `}<em parentName="td">{`project name`}</em>{` you created when getting your plugin ID from the Adobe Developer Console.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Develop / Publish`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`version`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Version number of your plugin in `}<inlineCode parentName="td">{`x.y.z`}</inlineCode>{` format. `}<br />{`Version must be three segments and each version component must be between `}<inlineCode parentName="td">{`0`}</inlineCode>{` and `}<inlineCode parentName="td">{`99`}</inlineCode>{`.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Develop / Publish`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`main`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Path to the your plugin initialization code. This can be a JavaScript file or an HTML file.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Optional (defaults to `}<inlineCode parentName="td">{`main.js`}</inlineCode>{`)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`icons`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`IconDefinition[]`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Icons for your plugin (which may be rendered in various contexts, such as the plugin panel) `}<br />{` PNG, JPG/JPEG formats are supported and the max file size for each icon is 1MB. `}<br />{` You should specify at least the 1x and 2x size. Icons for the Plugin Manager are uploaded directly via the Adobe Developer Console, not included within your plugin itself. See our `}<a parentName="td" {...{
              "href": "../../../../distribution/packaging-your-plugin/"
            }}>{`"Publishing your plugin" guide`}</a>{` to learn more.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Publish`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`host`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`HostDefinition\\|HostDefinition[]`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Describes the supported applications that can be used with this plugin. This can include the type of application, the minimum required version, or the maximum version of the host app that the plugin supports. `}<br /><br />{` `}<strong parentName="td">{`Note:`}</strong>{` An array can ONLY be used during development. A single definition will be needed when submitting to the marketplace`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Develop / Publish`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`entryPoints`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`EntryPointDefinition[]`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Describes the entries your plugin adds to the `}<em parentName="td">{`Plugins`}</em>{` menu & plugin panel. See the next section for details.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Develop / Publish`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "icons"
    }}>{`Icons`}</h2>
    <p>{`Icons are not required during development, but `}<em parentName="p">{`must be provided`}</em>{` when distributing through the Plugin Marketplace.
The `}<inlineCode parentName="p">{`icons`}</inlineCode>{` field is an array of a `}<inlineCode parentName="p">{`IconDefinition`}</inlineCode>{`s.`}</p>
    <h3 {...{
      "id": "icondefinition"
    }}>{`IconDefinition`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Key`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`width`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`number`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Width in logical pixels`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`height`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`number`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Height in logical pixels`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`path`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Path to the icon (relative to the plugin root)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`scale`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`number[]`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Array of scales provided. For example, `}<inlineCode parentName="td">{`[1, 2]`}</inlineCode>{` means that there is a `}<inlineCode parentName="td">{`@1x`}</inlineCode>{` and `}<inlineCode parentName="td">{`@2x`}</inlineCode>{` version of the icon specified at the `}<inlineCode parentName="td">{`path`}</inlineCode>{`. (Densities other than `}<inlineCode parentName="td">{`1x`}</inlineCode>{` can be specified by adding `}<inlineCode parentName="td">{`@2x`}</inlineCode>{` before the icon's extension)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`theme`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string[]`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Array of themes this icon supports. Photoshop supports `}<inlineCode parentName="td">{`lightest`}</inlineCode>{`, `}<inlineCode parentName="td">{`light`}</inlineCode>{`, `}<inlineCode parentName="td">{`dark`}</inlineCode>{`, and `}<inlineCode parentName="td">{`darkest`}</inlineCode>{`. If all themes are compatible with the icon, you can use `}<inlineCode parentName="td">{`all`}</inlineCode>{`. (Default is `}<inlineCode parentName="td">{`all`}</inlineCode>{`).`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`species`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string[]`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Identifies the type of icon and where it would make sense to display it. The default is `}<inlineCode parentName="td">{`generic`}</inlineCode>{`, meaning that Photoshop is free to use this icon anywhere.`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "host"
    }}>{`Host`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`host`}</inlineCode>{` field is an `}<em parentName="p">{`object`}</em>{` matching the `}<inlineCode parentName="p">{`HostDefinition`}</inlineCode>{` format specified below. This entry allows your plugin to specify which app your plugin can run on such as Adobe XD or Photoshop. During development, the field can contain an `}<em parentName="p">{`array`}</em>{` of HostDefinition's. This can be very convient during development of cross-compatible UXP plugins. However, during submission to the marketplace, only one HostDefinition is allowed.`}</p>
    <h3 {...{
      "id": "hostdefinition"
    }}>{`HostDefinition`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Key`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Required`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`app`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Indicates the supported application for this plugin (currently, the only valid values here are `}<inlineCode parentName="td">{`"XD"`}</inlineCode>{` and `}<inlineCode parentName="td">{`"PS"`}</inlineCode>{`).`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Develop / Publish`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`minVersion`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Minimum required version of the host app (in `}<inlineCode parentName="td">{`x.y`}</inlineCode>{` format) that can run this plugin. The lowest valid version for manifest V4 plugins is version `}<inlineCode parentName="td">{`22.0`}</inlineCode>{`. `}<br />{` `}<strong parentName="td">{`Note:`}</strong>{` The version number must be at least two segments. Typically, you'll leave the minor segment set to `}<inlineCode parentName="td">{`0`}</inlineCode>{`, e.g. `}<inlineCode parentName="td">{`22.0`}</inlineCode>{`.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Develop / Publish`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`maxVersion`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Maximum version of host app that can run this plugin. Same formatting as `}<inlineCode parentName="td">{`host.minVersion`}</inlineCode>{`.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Optional`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "entry-points"
    }}>{`Entry Points`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`entryPoints`}</inlineCode>{` field is an `}<em parentName="p">{`array`}</em>{` of objects matching the `}<inlineCode parentName="p">{`EntryPointDefinition`}</inlineCode>{` format specified below. These entries appear both in the `}<em parentName="p">{`Plugins`}</em>{` menu in the native menubar, and the plugin panel.`}</p>
    <p>{`Each entry point specifies a `}<inlineCode parentName="p">{`type`}</inlineCode>{`, to create either a direct-action command or a panel show/hide command.`}</p>
    <h3 {...{
      "id": "entrypointdefinition"
    }}>{`EntryPointDefinition`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Key`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`type`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Entry point type: either `}<inlineCode parentName="td">{`"command"`}</inlineCode>{` or `}<inlineCode parentName="td">{`"panel"`}</inlineCode>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Unique identifier for the entry point. This `}<inlineCode parentName="td">{`id`}</inlineCode>{` will also be mapped to entrypoints defined in your plugin code.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`label`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Label for this menu item that the user will select to run your plugin. May be a single string `}<em parentName="td">{`or`}</em>{` a dictionary of localized strings.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`shortcut`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Object`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Optional.`}</em>{` `}<br /><br />{` Object defining Mac and Windows keyboard shortcuts for this menu item. See "Keyboard shortcuts" below for details. Only valid for `}<inlineCode parentName="td">{`command`}</inlineCode>{` entry points.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`minimumSize`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Object`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Optional.`}</em>{` Valid only for `}<inlineCode parentName="td">{`panel`}</inlineCode>{` entry points. `}<br /><br />{` Object defining the preferred minimum size of the panel. This object is of the form `}<inlineCode parentName="td">{`{width: number, height: number}`}</inlineCode>{` where each length is in pixel units. The host app may not guarantee the minimum width depending upon context.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`maximumSize`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Object`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Optional.`}</em>{` Valid only for `}<inlineCode parentName="td">{`panel`}</inlineCode>{` entry points. `}<br /><br />{` Object defining the preferred maximum size of the panel. This object is of the form `}<inlineCode parentName="td">{`{width: number, height: number}`}</inlineCode>{` where each length is in pixel units. The host app may not guarantee the maximum width depending upon context.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`preferredDockedSize`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Object`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Optional.`}</em>{` Valid only for `}<inlineCode parentName="td">{`panel`}</inlineCode>{` entry points. `}<br /><br />{` Object defining the preferred size of the panel when docked. This object is of the form `}<inlineCode parentName="td">{`{width: number, height: number}`}</inlineCode>{` where each length is in pixel units. This setting is a preference, and may not be honored.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`preferredFloatingSize`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Object`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Optional.`}</em>{` Valid only for `}<inlineCode parentName="td">{`panel`}</inlineCode>{` entry points. `}<br /><br />{` Object defining the preferred size of the panel when floating. This object is of the form `}<inlineCode parentName="td">{`{width: number, height: number}`}</inlineCode>{` where each length is in pixel units. This setting is a preference, and may not be honored.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`icons`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`array<object>`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Icons for your panel. Each panel in a plugin requires its own set of icon set, which is shown in the toolbars when minimized, and has no additional treatment supplied. A panel icon is 23x23 (46x46) in size, and can be transparent. These are different from the icons in the main plugin. They are optional during development, but `}<em parentName="td">{`must`}</em>{` be present in the manifest and the project if the plugin is submitted to the Plugin Marketplace via the Developer Console.`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "keyboard-shortcuts"
    }}>{`Keyboard shortcuts`}</h3>
    <blockquote>
      <p parentName="blockquote">{`Shortcuts are not yet available for plugins.`}</p>
    </blockquote>
    <h2 {...{
      "id": "menu-localization"
    }}>{`Menu Localization`}</h2>
    <p>{`Plugin menu item labels or panel labels can be localized to match the host's current UI language setting. Other manifest fields such as `}<inlineCode parentName="p">{`name`}</inlineCode>{` `}<em parentName="p">{`cannot be localized yet.`}</em>{`
Localized labels are represented as an object containing multiple translations, instead of a single string value:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"label": {
    "default": "Menu Label",
    "fr": "Etiquette de Menu",
    "de": "Menübezeichnung"
}
`}</code></pre>
    <p>{`A default string is always required.`}</p>
    <h2 {...{
      "id": "inheritance-and-overrides"
    }}>{`Inheritance and Overrides`}</h2>
    <p>{`Certain top-level fields can be overridden deeper in the manifest.`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`icons`}</inlineCode>{` can be overridden for each panel's entry point.`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      